import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as shopApi from '../../services/shopApi';

interface ShopProduct {
  sku: string;
  title: string;
  company_name: string;
  company_code: string | null;
  price_range: { min: string; max: string; currency: string };
  darkstore_status: 'live' | 'coming_soon' | 'not_available';
  primary_image: string | null;
}

interface Store {
  id: number;
  name: string;
  slug: string;
  logo_url?: string;
  product_count: number;
}

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery: string;
  onSearch: (query: string) => void;
}

const POPULAR_SEARCHES = [
  'Hoodies',
  'Graphic Tees',
  'Sneakers',
  'Accessories',
  'New Arrivals',
  'Streetwear',
];

const CACHE_KEY_BRANDS = 'arkx_shop_stores_cache';
const CACHE_KEY_POPULAR = 'arkx_search_popular_cache';
const CACHE_TTL = 5 * 60 * 1000;

function getCached<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL) return null;
    return data as T;
  } catch {
    return null;
  }
}

function setCache<T>(key: string, data: T) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch { /* ignore quota errors */ }
}

function formatPrice(product: ShopProduct): string {
  const min = parseFloat(product.price_range.min);
  const max = parseFloat(product.price_range.max);
  if (min === max) return `$${min.toFixed(2)}`;
  return `$${min.toFixed(2)} - $${max.toFixed(2)}`;
}

export default function SearchOverlay({ isOpen, onClose, initialQuery, onSearch }: SearchOverlayProps) {
  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [liveResults, setLiveResults] = useState<ShopProduct[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [brands, setBrands] = useState<Store[]>([]);
  const [popularItems, setPopularItems] = useState<ShopProduct[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset query when overlay opens
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
      setDebouncedQuery('');
      setLiveResults([]);
      // Auto-focus after a frame so the DOM is ready
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen, initialQuery]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Fetch empty-state data on mount
  useEffect(() => {
    if (!isOpen) return;

    const cachedBrands = getCached<Store[]>(CACHE_KEY_BRANDS);
    if (cachedBrands) {
      setBrands(cachedBrands);
    } else {
      shopApi.getBrands().then(res => {
        const b = res.data.brands || [];
        setBrands(b);
        setCache(CACHE_KEY_BRANDS, b);
      }).catch(() => {});
    }

    const cachedPopular = getCached<ShopProduct[]>(CACHE_KEY_POPULAR);
    if (cachedPopular) {
      setPopularItems(cachedPopular);
    } else {
      shopApi.getProducts({ page_size: 8 }).then(res => {
        const p = res.data.products || [];
        setPopularItems(p);
        setCache(CACHE_KEY_POPULAR, p);
      }).catch(() => {});
    }
  }, [isOpen]);

  // Debounce query
  useEffect(() => {
    if (!query.trim()) {
      setDebouncedQuery('');
      setLiveResults([]);
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Live search
  useEffect(() => {
    if (!debouncedQuery) return;
    let cancelled = false;
    setIsSearching(true);
    shopApi.getProducts({ search: debouncedQuery, page_size: 6 }).then(res => {
      if (!cancelled) {
        setLiveResults(res.data.products || []);
        setIsSearching(false);
      }
    }).catch(() => {
      if (!cancelled) setIsSearching(false);
    });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    onSearch(query);
  }, [query, onSearch]);

  const handlePopularClick = useCallback((term: string) => {
    setQuery(term);
    onSearch(term);
  }, [onSearch]);

  const handleNavigate = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const isIdle = !query.trim();
  const hasResults = !isIdle && liveResults.length > 0 && !isSearching;
  const noResults = !isIdle && liveResults.length === 0 && !isSearching && debouncedQuery === query.trim();

  const featuredBrands = brands.filter(b => b.product_count > 0).slice(0, 8);

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop — desktop only */}
      <div
        className="hidden md:block fixed inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-0 md:inset-auto md:top-16 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-4xl md:mx-4 md:rounded-2xl md:shadow-2xl md:max-h-[70vh] bg-white flex flex-col overflow-hidden">
        {/* Search bar */}
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products, brands..."
            className="flex-1 text-base outline-none placeholder-gray-400"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="p-1 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-gray-500 hover:text-gray-900 flex-shrink-0"
          >
            Cancel
          </button>
        </form>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Idle — empty state */}
          {isIdle && (
            <div className="md:grid md:grid-cols-5 md:divide-x md:divide-gray-100">
              {/* Left: Popular Searches */}
              <div className="p-4 md:col-span-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Popular Searches</h3>
                <div className="space-y-0.5">
                  {POPULAR_SEARCHES.map((term) => (
                    <button
                      key={term}
                      onClick={() => handlePopularClick(term)}
                      className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-left"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-300 flex-shrink-0" />
                      {term}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: Brands + Items */}
              <div className="p-4 md:col-span-3 space-y-6">
                {/* Featured Stores */}
                {featuredBrands.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Featured Stores</h3>
                    <div className="flex gap-4 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible scrollbar-hide">
                      {featuredBrands.map((brand) => (
                        <Link
                          key={brand.id}
                          to={`/brand/${brand.slug}`}
                          onClick={handleNavigate}
                          className="flex flex-col items-center gap-1.5 flex-shrink-0 group"
                        >
                          <div className="w-14 h-14 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center ring-2 ring-transparent group-hover:ring-gray-200 transition-all">
                            {brand.logo_url ? (
                              <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-lg font-bold text-gray-400">{brand.name.charAt(0)}</span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 group-hover:text-gray-900 transition-colors text-center max-w-[60px] truncate">{brand.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular Items */}
                {popularItems.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Popular Items</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {popularItems.slice(0, 6).map((product) => (
                        <Link
                          key={product.sku}
                          to={`/product/${product.sku}`}
                          onClick={handleNavigate}
                          className="group"
                        >
                          <div className="rounded-xl border border-gray-100 bg-white overflow-hidden hover:border-gray-200 hover:shadow-sm transition-all">
                            <div className="relative aspect-[3/4] bg-gray-50 overflow-hidden">
                              {product.primary_image ? (
                                <img
                                  src={product.primary_image}
                                  alt={product.title}
                                  loading="lazy"
                                  className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                  <span className="text-xs">No image</span>
                                </div>
                              )}
                              {product.darkstore_status === 'live' && (
                                <div className="absolute top-1.5 left-1.5 bg-black/80 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                                  1-3 hr
                                </div>
                              )}
                            </div>
                            <div className="p-2 space-y-0.5">
                              <p className="text-[10px] text-gray-400 truncate">{product.company_name}</p>
                              <p className="text-xs text-gray-900 line-clamp-1">{product.title}</p>
                              <p className="text-xs font-medium text-gray-900">{formatPrice(product)}</p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading state */}
          {!isIdle && isSearching && (
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                      <div className="aspect-[3/4] bg-gray-100" />
                      <div className="p-2 space-y-1.5">
                        <div className="h-2 bg-gray-100 rounded w-12" />
                        <div className="h-3 bg-gray-100 rounded w-full" />
                        <div className="h-3 bg-gray-100 rounded w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Live results */}
          {hasResults && (
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {liveResults.map((product) => (
                  <Link
                    key={product.sku}
                    to={`/product/${product.sku}`}
                    onClick={handleNavigate}
                    className="group"
                  >
                    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden hover:border-gray-200 hover:shadow-sm transition-all">
                      <div className="relative aspect-[3/4] bg-gray-50 overflow-hidden">
                        {product.primary_image ? (
                          <img
                            src={product.primary_image}
                            alt={product.title}
                            loading="lazy"
                            className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <span className="text-xs">No image</span>
                          </div>
                        )}
                        {product.darkstore_status === 'live' && (
                          <div className="absolute top-1.5 left-1.5 bg-black/80 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                            1-3 hr
                          </div>
                        )}
                      </div>
                      <div className="p-2 space-y-0.5">
                        <p className="text-[10px] text-gray-400 truncate">{product.company_name}</p>
                        <p className="text-xs text-gray-900 line-clamp-1">{product.title}</p>
                        <p className="text-xs font-medium text-gray-900">{formatPrice(product)}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* No results */}
          {noResults && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <MagnifyingGlassIcon className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No results yet — keep typing</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
